import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Pause, Play, RotateCcw, SkipForward, Sparkles, X } from 'lucide-react';
import { DailyMeditationDto } from '../../api';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  MeditationPhaseKey,
  MeditationSessionPhase,
  buildMeditationPhases,
  formatSessionTime,
} from './meditationSession';
import useMeditationSession from './useMeditationSession';
import BreathingPacer from './BreathingPacer';

const PHASE_TITLE_KEYS: Record<MeditationPhaseKey, string> = {
  preparation: 'meditationPlayerPhasePreparation',
  breathing: 'meditationPlayerPhaseBreathing',
  intention: 'meditationPlayerPhaseIntention',
  closing: 'meditationPlayerPhaseClosing',
};

export interface MeditationPlayerProps {
  isOpen: boolean;
  meditation: DailyMeditationDto | null;
  onClose: () => void;
  onPhaseStart?: (phase: MeditationSessionPhase, index: number) => void;
  onPhaseEnd?: (phase: MeditationSessionPhase, index: number) => void;
  onComplete?: () => void;
}

const MeditationPlayer: React.FC<MeditationPlayerProps> = ({
  isOpen,
  meditation,
  onClose,
  onPhaseStart,
  onPhaseEnd,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);

  const phases = useMemo(() => buildMeditationPhases(meditation), [meditation]);
  const session = useMeditationSession(phases, { onPhaseStart, onPhaseEnd, onComplete });
  const { stop } = session;

  // A closed player never keeps a session running behind the dashboard.
  useEffect(() => {
    if (!isOpen) {
      setIsExitDialogOpen(false);
      stop();
    }
  }, [isOpen, stop]);

  const requestClose = () => {
    if (session.isActive) {
      setIsExitDialogOpen(true);

      return;
    }

    onClose();
  };

  // Fechar pelo X, por Escape ou pelo fundo passa pela mesma confirmacao que o
  // botao de terminar: o dialogo e controlado, por isso recusar fechar mantem-no aberto.
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      requestClose();
    }
  };

  const confirmExit = () => {
    setIsExitDialogOpen(false);
    session.stop();
    onClose();
  };

  const phaseTitle = session.currentPhase
    ? session.currentPhase.title || t(PHASE_TITLE_KEYS[session.currentPhase.key])
    : '';

  const renderIdle = () => (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-8 text-center">
      <Sparkles className="h-8 w-8 text-indigo-400" />
      {session.totalDurationSeconds !== null && (
        <p className="text-sm font-medium text-indigo-800">
          {t('meditationPlayerTotalDuration', {
            time: formatSessionTime(session.totalDurationSeconds),
          })}
        </p>
      )}
      <p className="text-xs text-indigo-700">
        {t('meditationPlayerPhaseCounter', { current: 1, total: phases.length })}
      </p>
    </div>
  );

  const renderRunning = () => (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm uppercase tracking-wide text-indigo-500">
          {t('meditationPlayerPhaseCounter', {
            current: session.phaseIndex + 1,
            total: phases.length,
          })}
        </p>
        <h3 className="text-lg font-semibold text-slate-800">{phaseTitle}</h3>
        {session.currentPhase?.focus && (
          <span className="inline-block text-sm font-medium text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full">
            {session.currentPhase.focus}
          </span>
        )}
      </div>

      {session.currentPhase?.text && (
        <p className="text-base text-slate-700 leading-relaxed">{session.currentPhase.text}</p>
      )}

      {session.currentPhase?.breathingPattern && (
        <BreathingPacer
          pattern={session.currentPhase.breathingPattern}
          elapsedSeconds={session.phaseElapsedMs / 1000}
          isRunning={session.status === 'running'}
        />
      )}

      <div className="space-y-2">
        {session.phaseRemainingSeconds === null ? (
          <p className="text-base text-slate-600">{t('meditationPlayerSelfPacedPhase')}</p>
        ) : (
          <>
            <Progress
              value={session.phaseProgress * 100}
              aria-label={t('meditationPlayerPhaseProgressLabel')}
            />
            <p className="text-base text-slate-600 text-right">
              {t('meditationPlayerTimeRemaining', {
                time: formatSessionTime(session.phaseRemainingSeconds),
              })}
            </p>
          </>
        )}
      </div>

      <Progress
        value={session.sessionProgress * 100}
        aria-label={t('meditationPlayerSessionProgressLabel')}
      />
    </div>
  );

  const renderFinished = () => (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-8 text-center">
      <CheckCircle className="h-8 w-8 text-emerald-500" />
      <p className="text-sm font-medium text-emerald-800">{t('meditationPlayerFinishedTitle')}</p>
      <p className="text-xs text-emerald-700">{t('meditationPlayerFinishedDescription')}</p>
    </div>
  );

  const renderBody = () => {
    if (phases.length === 0) {
      return <p className="text-sm text-slate-600">{t('meditationPlayerEmpty')}</p>;
    }

    if (session.status === 'finished') {
      return renderFinished();
    }

    if (session.status === 'idle') {
      return renderIdle();
    }

    return renderRunning();
  };

  const renderFooter = () => {
    if (phases.length === 0) {
      return <Button onClick={onClose}>{t('meditationPlayerClose')}</Button>;
    }

    if (session.status === 'finished') {
      return (
        <>
          <Button variant="outline" className="bg-white" onClick={session.start}>
            <RotateCcw className="h-4 w-4" />
            {t('meditationPlayerRestart')}
          </Button>
          <Button onClick={onClose}>{t('meditationPlayerClose')}</Button>
        </>
      );
    }

    if (session.status === 'idle') {
      return (
        <>
          <Button variant="outline" className="bg-white" onClick={onClose}>
            {t('meditationPlayerClose')}
          </Button>
          <Button onClick={session.start}>
            <Play className="h-4 w-4" />
            {t('meditationPlayerStart')}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant="outline" className="bg-white" onClick={requestClose}>
          <X className="h-4 w-4" />
          {t('meditationPlayerFinish')}
        </Button>
        <Button variant="outline" className="bg-white" onClick={session.skipPhase}>
          <SkipForward className="h-4 w-4" />
          {t('meditationPlayerSkipPhase')}
        </Button>
        {session.status === 'running' ? (
          <Button onClick={session.pause}>
            <Pause className="h-4 w-4" />
            {t('meditationPlayerPause')}
          </Button>
        ) : (
          <Button onClick={session.resume}>
            <Play className="h-4 w-4" />
            {t('meditationPlayerResume')}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{meditation?.title || t('meditationPlayerTitle')}</DialogTitle>
            <DialogDescription>
              {meditation?.subtitle || t('meditationPlayerDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">{renderBody()}</div>

          {session.wakeLockUnavailable && (
            <p
              role="status"
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              {t('meditationPlayerWakeLockUnavailable')}
            </p>
          )}

          <DialogFooter className="gap-3">{renderFooter()}</DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isExitDialogOpen} onOpenChange={setIsExitDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('meditationPlayerExitTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('meditationPlayerExitDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('meditationPlayerExitCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmExit}>
              {t('meditationPlayerExitConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MeditationPlayer;
